<kanzi-ui-main-card
  [title]="'locations.ubicaciones.create.title' | translate"
  [description]="'locations.ubicaciones.create.description' | translate"
>
  <ng-template kTemplate="rightTopContent">
    <div class="flex gap-2">
      <p-button
        [label]="'common.cancel' | translate"
        icon="pi pi-times"
        [outlined]="true"
        severity="primary"
        (onClick)="onCancel()"
        [disabled]="isCreating()"
      ></p-button>
      <p-button
        [label]="'common.save' | translate"
        icon="pi pi-save"
        severity="primary"
        (onClick)="onSave()"
        [loading]="isCreating()"
      ></p-button>
    </div>
  </ng-template>

  <div class="grid">
    <div class="col-12 lg:col-8">
      <p-card>
        <div class="flex flex-column gap-4">
          <!-- Name -->
          <div class="field">
            <label for="name" class="font-semibold">{{ 'locations.ubicaciones.form.name' | translate }} *</label>
            <input
              id="name"
              type="text"
              pInputText
              class="w-full"
              [(ngModel)]="ubicacion.name"
              [placeholder]="'locations.ubicaciones.form.namePlaceholder' | translate"
              [disabled]="isCreating()"
            />
          </div>

          <!-- Display Name -->
          <div class="field">
            <label for="display_name" class="font-semibold">{{ 'locations.ubicaciones.form.displayName' | translate }}</label>
            <input
              id="display_name"
              type="text"
              pInputText
              class="w-full"
              [(ngModel)]="ubicacion.display_name"
              [placeholder]="'locations.ubicaciones.form.displayNamePlaceholder' | translate"
              [disabled]="isCreating()"
            />
            <small class="text-500">{{ 'locations.ubicaciones.form.displayNameHelp' | translate }}</small>
          </div>

          <!-- Type -->
          <div class="field">
            <label for="type" class="font-semibold">{{ 'locations.ubicaciones.form.type' | translate }} *</label>
            <p-dropdown
              id="type"
              [options]="locationTypes()"
              [ngModel]="ubicacion.type_id"
              (ngModelChange)="onTypeChange($event)"
              [placeholder]="'locations.ubicaciones.form.typePlaceholder' | translate"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              [loading]="isTypesLoading()"
              [disabled]="isCreating()"
            ></p-dropdown>
            <small class="text-500">{{ 'locations.ubicaciones.form.typeHelp' | translate }}</small>
          </div>

          <!-- Parent Location (TreeSelect) -->
          <div class="field">
            <label for="parent" class="font-semibold">{{ 'locations.ubicaciones.form.parent' | translate }} *</label>
            @if (!selectedTypeId()) {
              <p-message 
                severity="info" 
                [text]="'locations.ubicaciones.form.parentSelectTypeFirst' | translate"
                styleClass="w-full"
              ></p-message>
            } @else {
              <p-treeSelect
                id="parent"
                [options]="parentTree()"
                [ngModel]="selectedParent()"
                (ngModelChange)="selectedParent.set($event)"
                [placeholder]="'locations.ubicaciones.form.parentPlaceholder' | translate"
                class="md:w-20rem w-full"
                containerStyleClass="w-full"
                [loading]="isTreeLoading()"
                [disabled]="isCreating()"
              ></p-treeSelect>
              <small class="text-500">
                @if (parentTypeId()) {
                  {{ 'locations.ubicaciones.form.parentCompatibleTypes' | translate }}
                } @else {
                  {{ 'locations.ubicaciones.form.parentHelp' | translate }}
                }
              </small>
            }
          </div>

          <!-- External ID -->
          <div class="field">
            <label for="external_id" class="font-semibold">{{ 'locations.ubicaciones.form.externalId' | translate }}</label>
            <input
              id="external_id"
              type="text"
              pInputText
              class="w-full"
              [(ngModel)]="ubicacion.external_id"
              [placeholder]="'locations.ubicaciones.form.externalIdPlaceholder' | translate"
              [disabled]="isCreating()"
            />
          </div>

          <!-- Description -->
          <div class="field">
            <label for="description" class="font-semibold">{{ 'locations.ubicaciones.form.description' | translate }}</label>
            <textarea
              id="description"
              pInputTextarea
              rows="3"
              class="w-full"
              [(ngModel)]="ubicacion.description"
              [placeholder]="'locations.ubicaciones.form.descriptionPlaceholder' | translate"
              [disabled]="isCreating()"
            ></textarea>
          </div>

          <!-- Zone Type (opcional) -->
          <div class="field">
            <label for="zone_type" class="font-semibold">
              {{ 'locations.ubicaciones.form.zoneType' | translate }}
            </label>
            <p-dropdown
              id="zone_type"
              [options]="zoneTypeOptions"
              [(ngModel)]="ubicacion.zone_type"
              [placeholder]="'locations.ubicaciones.form.zoneTypePlaceholder' | translate"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              [showClear]="true"
              [disabled]="isCreating()"
            ></p-dropdown>
            <small class="text-500">{{ 'locations.ubicaciones.form.zoneTypeHelp' | translate }}</small>
          </div>

          <!-- Customer Multi-Select (shown when logged user's customer has no parent) -->
          @if (showCustomerSelector()) {
            <div class="field">
              <label for="customer_ids" class="font-semibold">
                {{ 'warehouses.form.customers' | translate }}
                <span class="text-red-500">*</span>
              </label>
              <p-multiSelect
                inputId="customer_ids"
                [(ngModel)]="selectedCustomerIds"
                [options]="customersQuery.data()?.results || []"
                optionLabel="name"
                optionValue="id"
                [placeholder]="'warehouses.form.customersPlaceholder' | translate"
                [filter]="true"
                filterBy="name"
                [loading]="customersQuery.isLoading()"
                display="chip"
                name="customer_ids"
              ></p-multiSelect>
            </div>
          }

          <!-- Coordinates (optional) -->
          <div class="grid">
            <div class="col-6">
              <div class="field">
                <label for="longitude" class="font-semibold">{{ 'locations.ubicaciones.form.longitude' | translate }}</label>
                <input
                  id="longitude"
                  type="number"
                  pInputText
                  class="w-full"
                  [(ngModel)]="ubicacion.longitude"
                  placeholder="-74.123456"
                  [disabled]="isCreating()"
                />
              </div>
            </div>
            <div class="col-6">
              <div class="field">
                <label for="latitude" class="font-semibold">{{ 'locations.ubicaciones.form.latitude' | translate }}</label>
                <input
                  id="latitude"
                  type="number"
                  pInputText
                  class="w-full"
                  [(ngModel)]="ubicacion.latitude"
                  placeholder="4.123456"
                  [disabled]="isCreating()"
                />
              </div>
            </div>
          </div>
        </div>
      </p-card>
    </div>

    <!-- Help Section -->
    <div class="col-12 lg:col-4">
      <p-card [header]="'locations.ubicaciones.help.hierarchyTitle' | translate">
        <div class="flex flex-column gap-3">
          <p class="text-sm text-600 m-0">
            {{ 'locations.ubicaciones.help.hierarchyDescription' | translate }}
          </p>
          <ul class="text-sm text-700 pl-3 my-2">
            <li>WAREHOUSE ({{ 'locations.ubicaciones.help.warehouse' | translate }})</li>
            <li>└─ SALON ({{ 'locations.ubicaciones.help.salon' | translate }})</li>
            <li>&nbsp;&nbsp;&nbsp;└─ ROW ({{ 'locations.ubicaciones.help.row' | translate }})</li>
            <li>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└─ FRONT/BACK</li>
            <li>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└─ RACK ({{ 'locations.ubicaciones.help.rack' | translate }})</li>
            <li>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└─ LEVEL ({{ 'locations.ubicaciones.help.level' | translate }})</li>
            <li>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└─ POSITION ({{ 'locations.ubicaciones.help.position' | translate }})</li>
          </ul>
          <p class="text-sm text-600 m-0">
            {{ 'locations.ubicaciones.help.selectCorrectType' | translate }}
          </p>
        </div>
      </p-card>

      @if (createMutation.isError()) {
        <p-message
          severity="error"
          [text]="createMutation.error().message || ('locations.ubicaciones.errors.createError' | translate)"
          styleClass="mt-3"
        ></p-message>
      }
    </div>
  </div>
</kanzi-ui-main-card>
